import {
  ArrowsClockwiseIcon,
  BroadcastIcon,
  CheckIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react'
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react'
import Select from 'react-select'

import API from '../../../../api/api'
import Loader from '../../../Loader/Loader'
import ModalContainer from '../../../ModalContainer/ModelContainer'
import Stepper from '../../../Stepper/Stepper'

const STEPS = ['Preparing', 'Select encoder', 'Encoding', 'Result']

const MIN_PLACE_STEP_MS = 2000

const STEP = {
  PREPARE: 0,
  SELECT_ENCODER: 1,
  PLACE: 2,
  RESULT: 3,
} as const

type FobStep = (typeof STEP)[keyof typeof STEP]

interface EncoderOption {
  value: string
  label: string
}

const encoderSelectStyles = {
  menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 9999 }),
}

const DEFAULT_PREPARE_MESSAGE = (
  <>
    You are about to issue a physical fob/card.
    <br />
    Place the fob/card you want to program on the encoder now, and click Next to continue.
    <br />
    ⚠️ Note: If this fob/card is currently assigned to someone else, its existing data will be
    overwritten.
  </>
)

const KEY_ALREADY_EXISTS_MESSAGE =
  'This Card/FOB already belongs to this resident. Please update it or use a new card.'

interface SaltoSpaceIssueFobModalProps {
  isOpen: boolean
  buildingId: number
  headerTitle?: string
  prepareMessage?: ReactNode
  successTitle?: string
  successBody?: string
  allowOverride?: boolean
  onProgram: (encoderId: string, force?: boolean) => Promise<void>
  onClose: () => void
  onSuccess: () => void
}

const SaltoSpaceIssueFobModal = ({
  isOpen,
  buildingId,
  headerTitle = 'Issue physical fob/card',
  prepareMessage = DEFAULT_PREPARE_MESSAGE,
  successTitle = 'Fob/card successfully issued!',
  successBody = 'Now you can use it to access the property.',
  allowOverride = false,
  onProgram,
  onClose,
  onSuccess,
}: SaltoSpaceIssueFobModalProps) => {
  const [currentStep, setCurrentStep] = useState<FobStep>(STEP.PREPARE)
  const [isSuccess, setIsSuccess] = useState<boolean | null>(null)
  const [isConflict, setIsConflict] = useState<boolean>(false)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [encoders, setEncoders] = useState<EncoderOption[]>([])
  const [selectedEncoder, setSelectedEncoder] = useState<EncoderOption | null>(null)
  const [isEncodersLoading, setIsEncodersLoading] = useState<boolean>(false)

  const fetchEncoders = async (): Promise<void> => {
    setIsEncodersLoading(true)
    try {
      const { data } = await API.getSaltoSpaceFobEncoders(buildingId)
      setEncoders((data as string[]).map((id) => ({ label: id, value: id })))
    } catch {
      setEncoders([])
    } finally {
      setIsEncodersLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(STEP.PREPARE)
      setIsSuccess(null)
      setIsConflict(false)
      setIsProcessing(false)
      setErrorMessage(null)
      setSelectedEncoder(null)
      fetchEncoders()
    }
  }, [isOpen])

  const extractErrorMessage = (error: unknown): string => {
    const data = (error as { response?: { data?: unknown } })?.response?.data
    const serialized = typeof data === 'string' ? data : JSON.stringify(data ?? {})
    if (/key already exists/i.test(serialized)) {
      return KEY_ALREADY_EXISTS_MESSAGE
    }
    const message = (data as { message?: string })?.message
    return message ?? 'Temporary technical problem. Please contact IT or try again later.'
  }

  const isConflictError = (error: unknown): boolean => {
    const axiosError = error as { response?: { status?: number } }
    return axiosError?.response?.status === 409
  }

  const startIssuing = async (force = false): Promise<void> => {
    setCurrentStep(STEP.PLACE)
    setIsSuccess(null)
    setIsConflict(false)
    setErrorMessage(null)
    setIsProcessing(true)
    const startedAt = Date.now()
    let hasSucceeded = false
    try {
      await onProgram(selectedEncoder?.value ?? '', force)
      setIsSuccess(true)
      hasSucceeded = true
    } catch (error) {
      if (allowOverride && !force && isConflictError(error)) {
        setIsConflict(true)
      } else {
        setIsSuccess(false)
        setErrorMessage(extractErrorMessage(error))
      }
    }
    const elapsed = Date.now() - startedAt
    if (elapsed < MIN_PLACE_STEP_MS) {
      await new Promise((resolve) => setTimeout(resolve, MIN_PLACE_STEP_MS - elapsed))
    }
    setIsProcessing(false)
    setCurrentStep(STEP.RESULT)
    if (hasSucceeded) {
      onSuccess()
    }
  }

  const renderContent = () => {
    if (currentStep === STEP.PREPARE) {
      return (
        <>
          <p className="salto-space-fob-modal__message">{prepareMessage}</p>
          <div className="salto-space-fob-modal__icon-area salto-space-fob-modal__icon-area--compact">
            <img
              src="/images/integrations/encoder.png"
              alt="encoder"
              className="salto-space-fob-modal__encoder-img"
            />
          </div>
          <div className="salto-space-fob-modal__actions">
            <button className="button button-bordered auto-width" type="button" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button button-primary auto-width"
              type="button"
              onClick={() => setCurrentStep(STEP.SELECT_ENCODER)}
            >
              Next
            </button>
          </div>
        </>
      )
    }

    if (currentStep === STEP.SELECT_ENCODER) {
      return (
        <>
          <div
            className={`salto-space-fob-modal__select-content${isEncodersLoading ? ' salto-space-fob-modal__select-content--loading' : ''}`}
          >
            <p className="salto-space-fob-modal__message">
              Please choose the encoder you are going to issue the fob/card on:
            </p>
            <div className="salto-space-fob-modal__fob-select">
              <Select
                isSearchable
                options={encoders}
                value={selectedEncoder}
                isLoading={isEncodersLoading}
                placeholder="Select encoder..."
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={encoderSelectStyles}
                onChange={(option: EncoderOption | null) => setSelectedEncoder(option)}
              />
            </div>
            <div className="salto-space-fob-modal__refresh-row">
              <span className="salto-space-fob-modal__hint">
                If your encoder doesn't appear on the list, try refreshing.
              </span>
              <button
                type="button"
                className="button button-bordered auto-width salto-space-fob-modal__refresh-btn"
                onClick={() => void fetchEncoders()}
              >
                <ArrowsClockwiseIcon size={14} />
                Refresh list
              </button>
            </div>
          </div>
          {isEncodersLoading && (
            <div className="salto-space-fob-modal__loader-wrap">
              <Loader />
            </div>
          )}
          <div className="salto-space-fob-modal__actions">
            <button className="button button-bordered auto-width" type="button" onClick={onClose}>
              Cancel
            </button>
            <button
              className={`button button-primary auto-width${isEncodersLoading ? ' salto-space-fob-modal__btn--faded' : ''}`}
              type="button"
              disabled={!selectedEncoder || isEncodersLoading}
              onClick={() => void startIssuing()}
            >
              Next
            </button>
          </div>
        </>
      )
    }

    if (currentStep === STEP.PLACE) {
      return (
        <>
          <p className="salto-space-fob-modal__message">
            Keep the fob/card on the encoder while it's programmed.
            <br />
            Please wait for the result.
          </p>
          <div className="salto-space-fob-modal__icon-area">
            <Loader />
          </div>
          <div className="salto-space-fob-modal__actions">
            <button disabled className="button button-bordered auto-width" type="button">
              Cancel
            </button>
            <button disabled className="button button-primary auto-width" type="button">
              Next
            </button>
          </div>
        </>
      )
    }

    if (isConflict) {
      return (
        <>
          <p className="salto-space-fob-modal__message">
            <strong>This card doesn't match the selected resident</strong>
            <span className="salto-space-fob-modal__message-body">
              The card on the encoder is currently assigned to someone else. Override to revoke it
              from them and reassign it to this resident, or cancel to leave it unchanged.
            </span>
          </p>
          <div className="salto-space-fob-modal__result salto-space-fob-modal__result--error">
            <WarningIcon size={56} weight="bold" />
          </div>
          <div className="salto-space-fob-modal__actions">
            <button className="button button-bordered auto-width" type="button" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button button-primary auto-width"
              type="button"
              onClick={() => void startIssuing(true)}
            >
              Override
            </button>
          </div>
        </>
      )
    }

    if (isSuccess) {
      return (
        <>
          <p className="salto-space-fob-modal__message">
            <strong>{successTitle}</strong>
            <span className="salto-space-fob-modal__message-body">{successBody}</span>
          </p>
          <div className="salto-space-fob-modal__result salto-space-fob-modal__result--success">
            <CheckIcon size={56} weight="bold" />
          </div>
          <div className="salto-space-fob-modal__actions">
            <button className="button button-primary auto-width" type="button" onClick={onClose}>
              Close
            </button>
          </div>
        </>
      )
    }

    return (
      <>
        <p className="salto-space-fob-modal__message">
          <strong>Something went wrong!</strong>
          <span className="salto-space-fob-modal__message-body">
            {errorMessage ??
              'We were not able to issue the fob/card. Please check your encoder/card/fob and try again later.'}
          </span>
        </p>
        <div className="salto-space-fob-modal__result salto-space-fob-modal__result--error">
          <XIcon size={56} weight="bold" />
        </div>
        <div className="salto-space-fob-modal__actions">
          <button className="button button-bordered auto-width" type="button" onClick={onClose}>
            Close
          </button>
          <button
            className="button button-primary auto-width"
            type="button"
            onClick={() => setCurrentStep(STEP.SELECT_ENCODER)}
          >
            Try Again
          </button>
        </div>
      </>
    )
  }

  return (
    <ModalContainer
      isVisible={isOpen}
      headerTitle={headerTitle}
      headerIcon={<BroadcastIcon size={20} />}
      closeable={!isProcessing}
      additionalClass="salto-space-fob-modal-container"
      onClose={isProcessing ? undefined : onClose}
    >
      <div className="salto-space-fob-modal">
        <Stepper currentStep={currentStep} steps={STEPS} />
        {renderContent()}
      </div>
    </ModalContainer>
  )
}

export default SaltoSpaceIssueFobModal
